import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Divider, Form, Input, Space, Tooltip, Typography } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '@/shared/api';
import { errorCodes } from '@/shared/config';
import { ApiRoutes, PageRoutes } from '@/shared/router';
import { PageLayout } from '@/widgets/page-layout';

const { Title } = Typography;
const passwordRegex =
  /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%!^&+=.\-_*])([a-zA-Z0-9@#$%!^&+=*.\-_]){8,}$/;

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirm: string;
  recaptcha: string;
};

export const Register = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>();

  const [form] = Form.useForm();

  const onSubmit = async (data: FormData) => {
    setError(undefined);
    await form.validateFields();

    setLoading(true);
    const { confirm, recaptcha, ...restData } = data;
    try {
      await api.post(ApiRoutes.USERS_REGISTER, restData);
      navigate(PageRoutes.REGISTER_SUCCESS);
    } catch (error: any) {
      if (error?.error) {
        setError(error.error);
      }
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (error) {
      form.validateFields(['email']);
    }
  }, [error]);

  return (
    <PageLayout
      contentStyle={{
        padding: 0,
        background: '#fff',
        overflowY: 'auto',
      }}
    >
      <Wrapper direction="vertical" size={'large'}>
        <Link to={PageRoutes.HOME}>
          <img
            src="/favicon-colored.svg"
            alt="logo"
            style={{ width: 75, marginBottom: -5 }}
          />
        </Link>
        <Title level={3}>Create an account</Title>
        <Form
          name="registrationForm"
          form={form}
          initialValues={{
            remember: true,
          }}
          // onFinish={onSubmit}
          layout="vertical"
        >
          <div style={{ display: 'flex', width: '100%', gap: 10 }}>
            <Form.Item
              label="First name"
              name="firstName"
              rules={[
                {
                  required: true,
                  message: 'Please input your first name',
                },
              ]}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Last name"
              name="lastName"
              rules={[
                {
                  required: true,
                  message: 'Please input your last name',
                },
              ]}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              {
                type: 'email',
                message: 'The input is not valid e-mail address',
              },
              {
                required: true,
                message: 'Please input your e-mail address',
              },
              () => ({
                validator: () => {
                  if (error) {
                    return Promise.reject(
                      new Error(errorCodes[error as keyof typeof errorCodes]),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input />
          </Form.Item>

          <Tooltip
            placement="bottom"
            title="Password must contain at least 8 characters, lower case, upper case, number and a special character"
          >
            <Form.Item
              label="Password"
              name="password"
              hasFeedback
              rules={[
                {
                  required: true,
                  pattern: passwordRegex,
                  message: '',
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Tooltip>
          <Form.Item
            label="Confirm password"
            name="confirm"
            dependencies={['password']}
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please confirm your password',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Passwords that you entered do not match'),
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          {import.meta.env.VITE_ENV !== 'local' && (
            <Form.Item
              name="recaptcha"
              style={{ marginTop: 30 }}
              rules={[
                () => ({
                  validator() {
                    if (!recaptchaRef.current?.getValue()) {
                      return Promise.reject(
                        new Error('Please verify that you are not a robot'),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
              />
            </Form.Item>
          )}
          <Form.Item style={{ width: '100%', marginTop: 35 }}>
            <Button
              type="primary"
              // htmlType="submit"
              loading={loading}
              onClick={() => onSubmit(form.getFieldsValue())}
              style={{ width: '100%', height: 40, marginTop: -10 }}
            >
              Create account
            </Button>
          </Form.Item>
        </Form>
        <Divider plain style={{ width: 370, maxWidth: '100%', marginTop: -15 }}>
          Or
        </Divider>
        <Button
          icon={
            <img
              width={20}
              src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg"
            />
          }
          onClick={() =>
            loginWithRedirect({
              authorizationParams: {
                screen_hint: 'signup',
                connection: 'google-oauth2',
              },
            })
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 370,
            maxWidth: '100%',
            height: 40,
            marginTop: -15,
          }}
        >
          Continue with Google
        </Button>
        <LinkWrapper>
          Already have an account?
          <Link to={PageRoutes.LOGIN}>Log in</Link>
        </LinkWrapper>
        <div>
          We care about your{' '}
          <a
            href="https://suresteps.io/privacy/"
            target="_blank"
            rel="noreferrer"
          >
            privacy
          </a>
          .
        </div>
      </Wrapper>
    </PageLayout>
  );
};

const Wrapper = styled(Space)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  min-height: 100vh;

  .ant-form-item {
    margin-bottom: 16px;
  }
`;

const LinkWrapper = styled.div`
  margin-top: 30px;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 7px;
`;
